import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Check, Loader2, Pencil, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";

import {
  RESET_SITUATION_SESSION_PERSONALIZATION,
  SAVE_SITUATION_SESSION_PERSONALIZATION,
} from "@/graphql/mutations/situations";
import { SituationStory } from "@/components/situations/situation-story";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type Question = {
  key: string;
  prompt: string;
  sourcePrompt?: string | null;
  answerType?: string | null;
  /** The things to pick from. Empty on a question the child writes an answer to. */
  options?: string[] | null;
  /** A peer's opposing argument, on the question that has one. */
  counter?: string | null;
};

type Source = {
  grade?: number | null;
  intro?: string | null;
  story: string;
  questions: { key: string; prompt: string }[];
};

type Situation = {
  key: string;
  intro: string | null;
  story: string;
  sourceStory?: string | null;
  personalized: boolean;
  personalizationTheme?: string | null;
  /** A rewrite exists but was written from a different telling. */
  personalizationStale?: boolean;
  /** What the rewrite was actually written from — not necessarily the file now. */
  personalizedFrom?: Source | null;
  questions: Question[];
};

/**
 * Paragraphs one per line, which is what the edit box holds. Editing raw HTML is
 * the honest alternative and the wrong one to put in front of a parent: these
 * stories are plain paragraphs and nothing else, so the markup would just be
 * something they have to get right. The server does the same conversion back.
 */
function htmlToParagraphs(html: string): string {
  return html
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .split("\n")
    .map((line) =>
      line
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim()
    )
    .filter(Boolean)
    .join("\n");
}

/**
 * The grown-up's controls over one situation's wording: read what the file says,
 * type your own, or throw the rewrite away.
 *
 * **Edited per situation, stored on the session.** You edit the story in front of
 * you — that is the only place the wording makes sense to read — and it is kept
 * against the session that assigned it, so the same problem in next month's
 * session starts from the file again.
 *
 * All three controls write the same row underneath: an AI rewrite and a typed one
 * are stored identically, so „Grąžinti originalą" removes either. **The learner's
 * answers are never touched by any of it** — they hang off the problem, not the
 * wording, which is why the two live in different tables.
 */
export function SituationEditor({
  sessionId,
  situation,
  learnerName,
}: {
  sessionId: string;
  situation: Situation;
  learnerName?: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [showingOriginal, setShowingOriginal] = useState(false);

  const [story, setStory] = useState("");
  const [intro, setIntro] = useState("");
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [options, setOptions] = useState<Record<string, string[]>>({});
  const [counters, setCounters] = useState<Record<string, string>>({});

  const [save, { loading: saving }] = useMutation(SAVE_SITUATION_SESSION_PERSONALIZATION);
  const [reset, { loading: resetting }] = useMutation(RESET_SITUATION_SESSION_PERSONALIZATION);

  const openEditor = () => {
    setStory(htmlToParagraphs(situation.story));
    setIntro(situation.intro ?? "");
    setPrompts(
      Object.fromEntries(situation.questions.map((q) => [q.key, q.prompt]))
    );
    setOptions(
      Object.fromEntries(situation.questions.map((q) => [q.key, [...(q.options ?? [])]]))
    );
    setCounters(
      Object.fromEntries(situation.questions.map((q) => [q.key, q.counter ?? ""]))
    );
    setShowingOriginal(false);
    setEditing(true);
  };

  const submit = async () => {
    try {
      const result: any = await save({
        variables: {
          sessionId,
          situationKey: situation.key,
          story,
          intro: intro.trim() || null,
          questions: situation.questions.map((q) => ({
            questionKey: q.key,
            prompt: prompts[q.key] ?? q.prompt,
            // Omitted where there are none, which the server reads as „the
            // file's own". A count that does not match the file's is refused:
            // removing one of four choices is a different question.
            options: (q.options ?? []).length > 0 ? options[q.key] ?? q.options : undefined,
            // Same rule, and it is enforced the same way: omitted where the file
            // has none, because an argument the content never made is a whole
            // extra question with a box under it.
            counter: q.counter ? counters[q.key] ?? q.counter : undefined,
          })),
        },
      });
      const payload = result?.data?.saveSituationSessionPersonalization;
      const errors = payload?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      // Saved either way — a warning is something worth knowing about the text,
      // not a reason it was refused.
      (payload?.warnings ?? []).forEach((w: string) => toast.warning(w));
      toast.success("Išsaugota");
      setEditing(false);
    } catch (error) {
      toast.error("Nepavyko išsaugoti");
      console.error(error);
    }
  };

  const revert = async () => {
    try {
      const result: any = await reset({
        variables: { sessionId, situationKey: situation.key },
      });
      const errors = result?.data?.resetSituationSessionPersonalization?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      toast.success("Grąžintas originalas");
      setEditing(false);
      setShowingOriginal(false);
    } catch (error) {
      toast.error("Nepavyko grąžinti");
      console.error(error);
    }
  };

  if (editing) {
    return (
      <Card className="border-primary/40">
        <CardContent className="space-y-4 py-5">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Redaguojama</span>
            <span className="text-sm text-muted-foreground">
              — kiekviena pastraipa iš naujos eilutės
            </span>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">Įvadas</label>
            <Input value={intro} onChange={(e) => setIntro(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">Istorija</label>
            <Textarea
              value={story}
              onChange={(e) => setStory(e.target.value)}
              rows={10}
              className="text-[1.02rem] leading-relaxed"
            />
          </div>

          <div className="space-y-3">
            {situation.questions.map((question, index) => (
              <div key={question.key} className="space-y-1.5">
                <label className="text-sm text-muted-foreground">
                  {index + 1} klausimas
                </label>
                <Textarea
                  value={prompts[question.key] ?? ""}
                  onChange={(e) =>
                    setPrompts((prev) => ({ ...prev, [question.key]: e.target.value }))
                  }
                  rows={2}
                />
                {/* What the file says, so a change is visible rather than
                    remembered. */}
                {question.sourcePrompt && question.sourcePrompt !== prompts[question.key] && (
                  <p className="text-xs text-muted-foreground">
                    Originalas: {question.sourcePrompt}
                  </p>
                )}

                {/* The options are part of the question and are edited with it:
                    a story moved into another world leaves a choice naming
                    somebody who is no longer in it. They can be reworded but not
                    removed — the number of them is the question. */}
                {(question.options ?? []).length > 0 && (
                  <div className="space-y-1.5 pl-4">
                    <p className="text-xs text-muted-foreground">
                      Pasirinkimai — perrašyti galima, išmesti ne
                    </p>
                    {(options[question.key] ?? question.options ?? []).map((option, i) => (
                      <Input
                        key={i}
                        value={option}
                        onChange={(e) =>
                          setOptions((prev) => {
                            const row = [...(prev[question.key] ?? question.options ?? [])];
                            row[i] = e.target.value;
                            return { ...prev, [question.key]: row };
                          })
                        }
                      />
                    ))}
                  </div>
                )}

                {/* The peer's argument, for the options' reason: it names the
                    story's own characters, and left behind it argues about
                    somebody who is no longer in it. */}
                {question.counter && (
                  <div className="space-y-1.5 pl-4">
                    <p className="text-xs text-muted-foreground">
                      Kito atsakymas, į kurį vaikas atsakys — perrašyti galima, išmesti ne
                    </p>
                    <Textarea
                      value={counters[question.key] ?? ""}
                      onChange={(e) =>
                        setCounters((prev) => ({ ...prev, [question.key]: e.target.value }))
                      }
                      rows={2}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground">
            {learnerName ? `${learnerName} vardo` : "Mokinio vardo"} istorijoje geriau
            nevartoti — vaikas turi spręsti apie kitą žmogų, o ne ginti save.
          </p>

          <div className="flex items-center gap-2">
            <Button onClick={submit} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Išsaugoti
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
              <X className="mr-2 h-4 w-4" />
              Atšaukti
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // A rewrite that no longer belongs to the telling being shown. The file's
  // wording is being served, which is right — but nothing else on the page would
  // say that a rewrite is sitting there at all.
  const stale = situation.personalizationStale && situation.personalizedFrom;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" onClick={openEditor}>
        <Pencil className="mr-2 h-3.5 w-3.5" />
        Redaguoti tekstą
      </Button>

      {(situation.personalized || stale) && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowingOriginal((v) => !v)}
          >
            {showingOriginal ? "Slėpti originalą" : "Rodyti originalą"}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" disabled={resetting}>
                {resetting ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                )}
                Grąžinti originalą
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Grąžinti originalą?</AlertDialogTitle>
                <AlertDialogDescription>
                  Pritaikytas tekstas bus ištrintas ir vaikas matys tai, kas parašyta
                  faile. Jo atsakymai lieka — jie saugomi prie situacijos, ne prie
                  teksto.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                <AlertDialogAction onClick={revert}>Grąžinti</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {stale && (
        <p className="w-full text-sm text-amber-600 dark:text-amber-500">
          Pritaikytas tekstas buvo parašytas pagal kitą šios situacijos versiją, tad
          rodomas originalas. Pritaikyk iš naujo arba grąžink originalą.
        </p>
      )}

      {showingOriginal && (
        <div className="mt-2 grid w-full gap-3 md:grid-cols-2">
          <Card className="border-dashed">
            <CardContent className="py-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Originalas (kaip yra faile)
              </p>
              <SituationStory
                html={situation.sourceStory ?? ""}
                className="text-[0.98rem]"
              />
            </CardContent>
          </Card>

          {/* What the rewrite was written from — not always the same thing. A
              file edited since, or a learner grown into the next telling, and
              the two differ; that difference is the whole reason the source is
              kept beside the rewrite. */}
          {situation.personalizedFrom &&
            situation.personalizedFrom.story !== situation.sourceStory && (
              <Card className="border-dashed border-amber-500/50">
                <CardContent className="py-5">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Pagal ką buvo pritaikyta
                  </p>
                  <SituationStory
                    html={situation.personalizedFrom.story}
                    className="text-[0.98rem]"
                  />
                </CardContent>
              </Card>
            )}
        </div>
      )}
    </div>
  );
}
